import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Binoculars, MagnifyingGlass, LinkedinLogo, CheckCircle, WarningCircle, DownloadSimple, Lightning, Database, Globe, Sparkle, Brain, ArrowCounterClockwise } from "@phosphor-icons/react";
import { SitePeopleDialog } from "../components/SitePeopleDialog";
import { ApolloMorePanel } from "../components/ApolloMorePanel";
import { ApolloTitleSearchPanel } from "../components/ApolloTitleSearchPanel";
import { FitScanPanel } from "../components/FitScanPanel";
import { RatingBadge } from "../components/RatingBadge";

const DEPARTMENTS = ["executive", "it", "finance", "management", "sales", "legal", "support", "hr", "marketing", "communication", "education", "design", "health", "operations"];
const SENIORITIES = ["junior", "senior", "executive"];
const PAGE = 25;

const escRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const kwMatch = (title, kw) => {
  const t = (title || "").toLowerCase().replace(/&/g, "and");
  const k = kw.toLowerCase().replace(/&/g, "and").trim();
  if (!k) return false;
  if (k.length <= 4 && !k.includes(" ")) return new RegExp(`\\b${escRx(k)}\\b`).test(t);
  return t.includes(k);
};
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";

const INDUSTRIES = [
  { code: 15, label: "Pharmaceuticals" },
  { code: 12, label: "Biotechnology" },
  { code: 14, label: "Hospitals & Health Care" },
  { code: 17, label: "Medical Devices" },
  { code: 13, label: "Medical Practice" },
  { code: 70, label: "Research" },
  { code: 97, label: "Market Research" },
  { code: 11, label: "Management Consulting" },
];

const VerBadge = ({ v }) => {
  const map = { valid: "bg-emerald-50 text-emerald-700 border-emerald-200", accept_all: "bg-amber-50 text-amber-700 border-amber-200" };
  return <span className={`inline-block px-1.5 py-0.5 rounded border text-[11px] font-medium ${map[v] || "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>{(v || "unknown").replace("_", " ")}</span>;
};

const ConfBadge = ({ v }) => {
  const cls = v >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : v >= 80 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  return <span className={`inline-block px-1.5 py-0.5 rounded border text-[11px] font-medium ${cls}`}>{v}</span>;
};

export default function ProspectPage() {
  const [account, setAccount] = useState(null);
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState("");
  const [seniority, setSeniority] = useState("");
  const [keyword, setKeyword] = useState("");
  const [searching, setSearching] = useState(false);
  const [res, setRes] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [importCompany, setImportCompany] = useState("");
  const [importing, setImporting] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [mode, setMode] = useState("company");
  const [gIndustries, setGIndustries] = useState(new Set([15, 12]));
  const [gSeniority, setGSeniority] = useState("");
  const [gDepartment, setGDepartment] = useState("");
  const [gRows, setGRows] = useState([]);
  const [gSel, setGSel] = useState(new Set());
  const [gCursor, setGCursor] = useState(null);
  const [gScanned, setGScanned] = useState(0);
  const [gTotal, setGTotal] = useState(0);
  const [gDone, setGDone] = useState(false);
  const [gScanning, setGScanning] = useState(false);
  const [gImporting, setGImporting] = useState(false);
  const [gFiltered, setGFiltered] = useState(0);
  const [allKeywords, setAllKeywords] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [fitKeywords, setFitKeywords] = useState(null);
  const [fitScan, setFitScan] = useState(null);
  const [ratings, setRatings] = useState({});
  const [ratingBusy, setRatingBusy] = useState(false);
  const [scanTrigger, setScanTrigger] = useState(0);
  const [activeKws, setActiveKws] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("medihub_prospect_kws") || "[]")); } catch { return new Set(); }
  });
  const [history, setHistory] = useState([]);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [siteScan, setSiteScan] = useState(null);
  const [apollo, setApollo] = useState(null);

  const loadAccount = () => api.hunterAccount().then(setAccount).catch(() => {});
  const loadApollo = () => api.apolloAccount().then(d => setApollo(d.configured ? d : null)).catch(() => {});
  const loadHistory = () => api.hunterSearches().then(d => setHistory(d.items || [])).catch(() => {});
  useEffect(() => {
    loadAccount();
    loadApollo();
    loadHistory();
    api.settings().then(d => { setAllKeywords(d.prospect_keywords || []); setExclusions(d.prospect_exclusions || []); }).catch(() => {});
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setCacheInfo(null); return; }
    const t = setTimeout(() => {
      api.hunterCacheLookup(q).then(d => setCacheInfo(d.found ? d : null)).catch(() => setCacheInfo(null));
    }, 500);
    return () => clearTimeout(t);
  }, [query]);

  const persistKws = (next) => {
    setActiveKws(next);
    localStorage.setItem("medihub_prospect_kws", JSON.stringify([...next]));
  };
  const toggleKw = (kw) => {
    const next = new Set(activeKws);
    next.has(kw) ? next.delete(kw) : next.add(kw);
    persistKws(next);
  };

  const kwString = () => (fitKeywords ?? allKeywords.filter(k => activeKws.has(k))).join(",");

  const resetSearch = () => {
    setQuery(""); setDepartment(""); setSeniority(""); setKeyword("");
    setRes(null); setRows([]); setSelected(new Set());
    setFitKeywords(null); setFitScan(null); setRatings({});
    setCacheInfo(null); setImportCompany(""); setSiteScan(null);
    setScanTrigger(0);
  };

  const applyFitKeywords = (kws) => {
    setFitKeywords(kws);
    const q = (res && res.domain) || query.trim();
    setQuery(q);
    doSearch({ query: q, department, seniority, job_titles: kws.join(","), offset: 0, limit: PAGE });
  };

  const rateInChunks = async (domainArg, people) => {
    let rated = 0;
    for (let i = 0; i < people.length; i += 50) {
      const rr = await api.fitscanRate({ domain: domainArg, people: people.slice(i, i + 50) });
      setRatings(prev => ({ ...prev, ...rr.ratings }));
      rated += rr.rated || 0;
    }
    return rated;
  };

  const rateResults = async () => {
    const people = rows.filter(r => r.position && r.email).map(r => ({ key: r.email, name: `${r.first_name} ${r.last_name}`.trim(), title: r.position }));
    if (!people.length) { toast.error("No people with job titles to rate"); return; }
    setRatingBusy(true);
    try {
      const rated = await rateInChunks(res?.domain || query.trim(), people);
      toast.success(`Rated ${rated} people against the Medicys ICP`);
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setRatingBusy(false); }
  };

  const rateApollo = async (people) => {
    const rated = await rateInChunks(res?.domain || query.trim(), people);
    return { rated };
  };

  const doSearch = async (params) => {
    setSearching(true);
    try {
      const r = await api.hunterSearch(params);
      setRes(r);
      setRows(r.results);
      setSelected(new Set());
      setImportCompany(r.organization || params.query);
      loadAccount();
      loadHistory();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSearching(false);
    }
  };

  const rerun = (h) => {
    const q = h.domain || h.query;
    setQuery(q);
    setDepartment(h.department || "");
    setSeniority(h.seniority || "");
    const kws = (h.job_titles || "").split(",").map(s => s.trim()).filter(Boolean);
    persistKws(new Set(kws.filter(k => allKeywords.includes(k))));
    doSearch({ query: q, department: h.department || "", seniority: h.seniority || "", job_titles: h.job_titles || "", offset: 0, limit: PAGE });
  };

  const viewCache = async (domain) => {
    setSearching(true);
    try {
      const r = await api.hunterCache(domain);
      setRes(r);
      setRows(r.results);
      setSelected(new Set());
      setImportCompany(r.organization || domain);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSearching(false);
    }
  };

  const loadMore = async () => {
    if (res?.cached) return;
    setLoadingMore(true);
    try {
      const r = await api.hunterSearch({ query: query.trim(), department, seniority, job_titles: res?.job_titles || "", offset: rows.length, limit: PAGE });
      setRes(prev => ({ ...prev, total_results: r.total_results }));
      setRows(prev => [...prev, ...r.results]);
      loadAccount();
      loadHistory();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const loadAll = async () => {
    if (!res || res.cached) return;
    const remaining = res.total_results - rows.length;
    const est = Math.max(1, Math.ceil(remaining / 10));
    if (est > 25 && !window.confirm(`Loading all ${Number(res.total_results).toLocaleString()} people will cost roughly ${est} more Hunter credits. Continue?`)) return;
    setLoadingAll(true);
    try {
      let offset = rows.length;
      let total = res.total_results;
      while (offset < total && offset < 5000) {
        const r = await api.hunterSearch({ query: query.trim(), department, seniority, job_titles: res.job_titles || "", offset, limit: 100 });
        if (!r.results.length) break;
        setRows(prev => [...prev, ...r.results]);
        total = r.total_results;
        offset += r.results.length;
        setRes(prev => ({ ...prev, total_results: total }));
      }
      loadAccount();
      loadHistory();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoadingAll(false);
    }
  };

  const scanGlobal = async (cont) => {
    const kws = allKeywords.filter(k => activeKws.has(k));
    if (!kws.length) { toast.error("Pick at least one job-title keyword first"); return; }
    setGScanning(true);
    try {
      const r = await api.hunterGlobalSearch({
        job_titles: kws, industries: [...gIndustries], seniority: gSeniority, department: gDepartment,
        max_pages: 10, search_after: cont ? gCursor : null,
      });
      setGRows(prev => cont ? [...prev, ...r.matches] : r.matches);
      if (!cont) setGSel(new Set());
      setGCursor(r.next_search_after);
      setGScanned(prev => cont ? prev + r.scanned : r.scanned);
      setGFiltered(prev => cont ? prev + (r.filtered_non_healthcare || 0) : (r.filtered_non_healthcare || 0));
      setGTotal(r.total_results);
      setGDone(r.done);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setGScanning(false);
    }
  };

  const revealImport = async () => {
    const chosen = gRows.filter(r => gSel.has(r.reveal_handle) && !r.imported);
    if (!chosen.length) return;
    if (!window.confirm(`Reveal and import ${chosen.length} ${chosen.length === 1 ? "person" : "people"}? This will charge about ${chosen.length} Hunter credit${chosen.length === 1 ? "" : "s"}.`)) return;
    setGImporting(true);
    try {
      const r = await api.hunterRevealImport({ prospects: chosen });
      const sk = r.skipped || {};
      toast.success(`Imported ${r.created} to Contacts · ${r.credits_charged} credit${r.credits_charged === 1 ? "" : "s"} used${sk.already_in_db ? ` · ${sk.already_in_db} already in MediHub` : ""}`);
      setGRows(prev => prev.map(row => gSel.has(row.reveal_handle) ? { ...row, imported: true } : row));
      setGSel(new Set());
      loadAccount();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setGImporting(false);
    }
  };

  const visible = useMemo(() => {
    let out = rows;
    if (res?.cached) {
      const sel = allKeywords.filter(k => activeKws.has(k));
      if (sel.length) out = out.filter(r => sel.some(kw => kwMatch(r.position, kw)));
    }
    const k = keyword.trim().toLowerCase();
    if (k) out = out.filter(r => (r.position || "").toLowerCase().includes(k));
    return out;
  }, [rows, keyword, res, allKeywords, activeKws]);

  const importableVisible = visible.filter(r => r.importable);
  const allSelected = importableVisible.length > 0 && importableVisible.every(r => selected.has(r.email));

  const toggle = (email) => setSelected(prev => {
    const next = new Set(prev);
    next.has(email) ? next.delete(email) : next.add(email);
    return next;
  });

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(importableVisible.map(r => r.email)));

  const doImport = async () => {
    const prospects = rows.filter(r => selected.has(r.email));
    if (!prospects.length) return;
    setImporting(true);
    try {
      const r = await api.hunterImport({ company: importCompany.trim(), domain: res?.domain || "", prospects });
      const skippedTotal = Object.values(r.skipped).reduce((a, b) => a + b, 0);
      toast.success(`Imported ${r.created} contact${r.created === 1 ? "" : "s"}${skippedTotal ? ` · ${skippedTotal} skipped` : ""}`);
      const importedSet = new Set(prospects.map(p => p.email));
      setRows(prev => prev.map(row => importedSet.has(row.email)
        ? { ...row, in_db: true, importable: false, blockers: ["already in MediHub"] } : row));
      setSelected(new Set());
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setImporting(false);
    }
  };

  const remaining = account?.credits_remaining;
  const creditTone = remaining == null ? "" : remaining < 100 ? "border-rose-200 bg-rose-50 text-rose-700"
    : remaining < 500 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-zinc-200 bg-white text-zinc-700";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Prospect</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Find people at a company via Hunter and import them straight into MediHub. Only emails with confidence ≥ 80 can be imported.</p>
        </div>
        <div className="flex items-start gap-2">
          {apollo && (
            <div className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${apollo.credits_remaining < 100 ? "border-rose-200 bg-rose-50 text-rose-700" : apollo.credits_remaining < 500 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-violet-200 bg-violet-50/50 text-violet-800"}`} data-testid="apollo-credits">
              <div className="flex items-center gap-1.5 font-medium"><Sparkle size={14} weight="fill" />{Math.floor(Number(apollo.credits_remaining)).toLocaleString()} Apollo credits left</div>
              <div className="text-[11px] opacity-70 mt-0.5">of {Number(apollo.credits_total).toLocaleString()} this cycle</div>
              {apollo.credits_remaining < 100 && <div className="text-[11px] font-medium mt-0.5">Very low — reveals may start failing</div>}
            </div>
          )}
          {account && (
            <div className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${creditTone}`} data-testid="hunter-credits">
              <div className="flex items-center gap-1.5 font-medium"><Lightning size={14} weight="fill" />{Math.floor(Number(remaining)).toLocaleString()} Hunter credits left</div>
              <div className="text-[11px] opacity-70 mt-0.5">{account.plan_name} plan · resets {account.reset_date}</div>
              {remaining < 100 && <div className="text-[11px] font-medium mt-0.5">Very low — searches may start failing</div>}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={mode === "company" ? (e) => { e.preventDefault(); setScanTrigger(t => t + 1); } : mode === "global" ? (e) => { e.preventDefault(); scanGlobal(false); } : (e) => e.preventDefault()} className="rounded-lg border border-zinc-200 bg-white p-3 space-y-3">
        <div className="flex gap-1.5">
          <button type="button" data-testid="prospect-mode-company" onClick={() => setMode("company")}
            className={`text-xs px-2.5 py-1.5 rounded-md border ${mode === "company" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>At one company</button>
          <button type="button" data-testid="prospect-mode-global" onClick={() => setMode("global")}
            className={`text-xs px-2.5 py-1.5 rounded-md border ${mode === "global" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>Across all companies</button>
          <button type="button" data-testid="prospect-mode-titles" onClick={() => setMode("titles")}
            className={`text-xs px-2.5 py-1.5 rounded-md border ${mode === "titles" ? "bg-violet-700 text-white border-violet-700" : "border-violet-200 text-violet-700 hover:border-violet-400"}`}>By job title (Apollo)</button>
          {mode === "global" && <span className="text-xs text-zinc-400 self-center">scanning is free — you only pay 1 credit per email you choose to reveal</span>}
          {mode === "titles" && <span className="text-xs text-zinc-400 self-center">search any job title across Apollo's whole database — free to search, ~1 credit per revealed email</span>}
        </div>

        {mode === "company" ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">1 · Company name or domain</label>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g. lilly.com or Eli Lilly"
              data-testid="prospect-query"
              className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-2.5 text-sm focus:outline-none focus:border-zinc-400" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Department</label>
            <select value={department} onChange={e => setDepartment(e.target.value)} data-testid="prospect-department"
              className="mt-1 h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white capitalize">
              <option value="">Any</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Seniority</label>
            <select value={seniority} onChange={e => setSeniority(e.target.value)} data-testid="prospect-seniority"
              className="mt-1 h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white capitalize">
              <option value="">Any</option>
              {SENIORITIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {(query.trim() || res) && (
            <button type="button" data-testid="prospect-reset-btn" onClick={resetSearch}
              className="h-9 px-3 rounded-md border border-zinc-200 text-zinc-600 text-sm hover:border-zinc-400 hover:text-zinc-900 flex items-center gap-1.5 self-end">
              <ArrowCounterClockwise size={14} /> Reset
            </button>
          )}
          <span className="text-xs text-zinc-400 self-center pb-2">↓ every search starts with the Fit Scan below</span>
        </div>
        ) : mode === "global" ? (
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1.5">1 · Industries to scan</div>
            <div className="flex flex-wrap gap-1.5">
              {INDUSTRIES.map(ind => {
                const on = gIndustries.has(ind.code);
                return (
                  <button key={ind.code} type="button" data-testid={`global-industry-${ind.code}`}
                    onClick={() => setGIndustries(prev => { const n = new Set(prev); n.has(ind.code) ? n.delete(ind.code) : n.add(ind.code); return n; })}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
                    {ind.label}
                  </button>
                );
              })}
              <span className="text-xs text-zinc-400 self-center">none selected = every industry</span>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Department</label>
              <select value={gDepartment} onChange={e => setGDepartment(e.target.value)} data-testid="global-department"
                className="mt-1 h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white capitalize">
                <option value="">Any</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Seniority</label>
              <select value={gSeniority} onChange={e => setGSeniority(e.target.value)} data-testid="global-seniority"
                className="mt-1 h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white capitalize">
                <option value="">Any</option>
                {SENIORITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button type="submit" disabled={gScanning || activeKws.size === 0} data-testid="global-scan-btn"
              className="h-9 px-4 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 flex items-center gap-1.5">
              <Binoculars size={14} />{gScanning ? "Scanning…" : "3 · Scan 1,000 people (free)"}
            </button>
            {activeKws.size === 0 && <span className="text-xs text-amber-600 self-center">pick at least one job-title keyword below</span>}
          </div>
        </div>
        ) : null}

        {(mode === "global" || res?.cached) && allKeywords.length > 0 && (
          <div className="pt-3 border-t border-zinc-100" data-testid="prospect-kw-panel">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">2 · Job titles containing…</span>
              <span className="text-xs text-zinc-400">{mode === "company" ? "filtered by Hunter before you're charged — you never pay for HR or IT people. None selected = everyone at the company." : "required for a free scan — only people whose title matches are shown."}</span>
              <div className="ml-auto flex items-center gap-1.5">
                <button type="button" data-testid="prospect-kw-all" onClick={() => persistKws(new Set(allKeywords))} className="text-xs px-2 py-1 rounded border border-zinc-200 text-zinc-600 hover:border-zinc-300">All</button>
                <button type="button" data-testid="prospect-kw-none" onClick={() => persistKws(new Set())} className="text-xs px-2 py-1 rounded border border-zinc-200 text-zinc-600 hover:border-zinc-300">None</button>
                <Link to="/settings" className="text-xs text-zinc-400 underline hover:text-zinc-600">Edit list</Link>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {allKeywords.map(kw => {
                const on = activeKws.has(kw);
                return (
                  <button key={kw} type="button" onClick={() => toggleKw(kw)} data-testid={`prospect-kw-${kw.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
                    {kw}
                  </button>
                );
              })}
            </div>
            {exclusions.length > 0 && (
              <div className="mt-2 text-xs text-zinc-400" data-testid="prospect-exclusions-note">
                Excluding job titles matching: <span className="text-rose-500">{exclusions.join(" · ")}</span> — applies to Hunter, Apollo and the global scan. <Link to="/settings" className="underline hover:text-zinc-600">Edit</Link>
              </div>
            )}
            {fitKeywords && (
              <div className="mt-2 text-xs text-indigo-700 flex flex-wrap items-center gap-2" data-testid="fitscan-kw-active">
                <Brain size={13} weight="fill" /> Using Fit Scan keywords ({fitKeywords.length}): {fitKeywords.join(" · ")}
                <button type="button" onClick={() => setFitKeywords(null)} data-testid="fitscan-kw-clear"
                  className="px-1.5 py-0.5 rounded border border-indigo-200 hover:border-indigo-400">clear — back to my chips</button>
              </div>
            )}
            {res && !res.cached && res.job_titles !== kwString() && (
              <div className="mt-2 text-xs text-amber-600" data-testid="prospect-kw-stale">
                Keyword selection changed since the last search — press Search to apply it.
              </div>
            )}
            {res?.cached && (
              <div className="mt-2 text-xs text-emerald-700" data-testid="prospect-kw-cache-note">
                Cache view — chips filter your saved prospects instantly and for free. Press Search to run a live (charged) Hunter search with new keywords.
              </div>
            )}
          </div>
        )}
      </form>

      {mode === "titles" && <ApolloTitleSearchPanel onCreditsUsed={loadApollo} />}

      {mode === "company" && (
        <FitScanPanel domain={(res && res.domain) || query.trim()} onApplyKeywords={applyFitKeywords} onScan={setFitScan}
          myKeywords={allKeywords} trigger={scanTrigger} />
      )}

      {mode === "company" && (fitKeywords || exclusions.length > 0) && (
        <div className="px-1 space-y-1">
          {fitKeywords && (
            <div className="text-xs text-indigo-700 flex flex-wrap items-center gap-2" data-testid="fitscan-kw-active-company">
              <Brain size={13} weight="fill" /> Searched with Fit Scan keywords ({fitKeywords.length}): {fitKeywords.join(" · ")}
            </div>
          )}
          {exclusions.length > 0 && (
            <div className="text-xs text-zinc-400" data-testid="prospect-exclusions-note-company">
              Excluding job titles matching: <span className="text-rose-500">{exclusions.join(" · ")}</span> <Link to="/settings" className="underline hover:text-zinc-600">Edit</Link>
            </div>
          )}
        </div>
      )}

      {cacheInfo && (!res || res.domain !== cacheInfo.domain || !res.cached) && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 flex flex-wrap items-center gap-2 text-sm text-emerald-800" data-testid="prospect-cache-strip">
          <Database size={15} />
          <span><span className="font-medium">{cacheInfo.cached_count}</span> already-paid prospect{cacheInfo.cached_count === 1 ? "" : "s"} cached for {cacheInfo.organization || cacheInfo.domain} (last search {fmtWhen(cacheInfo.last_seen_at)}) — viewing them is free.</span>
          <button onClick={() => viewCache(cacheInfo.domain)} data-testid="prospect-view-cache"
            className="ml-auto h-7 px-2.5 rounded-md bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-800">View cached (free)</button>
        </div>
      )}

      {mode === "company" && !res && !searching && (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-white p-12 text-center text-zinc-400">
          <Binoculars size={32} className="mx-auto mb-2" />
          <p className="text-sm">Enter a company, pick the job-title keywords that matter, then search — e.g. <button type="button" className="underline hover:text-zinc-600" onClick={() => setQuery("lilly.com")}>lilly.com</button> + "Market access".</p>
          <p className="text-xs mt-1 text-zinc-400">Hunter filters by title before charging: 1 credit per 10 matching people, and everyone returned has a full name, job title and email.</p>
        </div>
      )}

      {mode === "company" && !res && !searching && history.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="prospect-history">
          <div className="px-3 py-2 border-b border-zinc-100 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-zinc-900">Recent searches</span>
            <span className="text-xs text-zinc-400">re-running an identical search in the same billing month is free{account?.reset_date ? ` (resets ${account.reset_date})` : ""} — changing anything makes it a new, charged search</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-b border-zinc-100 last:border-0">
                  <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{(h.organization || h.domain || h.query || "").replace(/\s+(and|&|of|the)$/i, "")}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500 max-w-[340px] truncate" title={h.job_titles}>{h.job_titles ? h.job_titles.split(",").join(" · ") : "all job titles"}{h.department ? ` · dept: ${h.department}` : ""}{h.seniority ? ` · ${h.seniority}` : ""}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{fmtWhen(h.last_at)} · {h.loaded} of {Number(h.total_results).toLocaleString()} loaded</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => rerun(h)} data-testid={`history-rerun-${i}`} className="text-xs px-2 py-1 rounded border border-zinc-200 text-zinc-600 hover:border-zinc-400 mr-1.5">Re-run (free)</button>
                    {h.domain && <button onClick={() => { setQuery(h.domain); viewCache(h.domain); }} data-testid={`history-cache-${i}`} className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:border-emerald-400">View cache</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode === "company" && res && (
        <>
          {res.cached && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2" data-testid="prospect-cache-banner">
              <Database size={15} /> Showing your paid cache for {res.domain} — no credits used. Keyword chips filter it locally; press Search to run a live (charged) Hunter search.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm text-zinc-600" data-testid="prospect-result-summary">
              <span className="font-medium text-zinc-900">{(res.organization || res.domain || "").replace(/\s+(and|&|of|the)$/i, "")}</span>
              {res.domain && <span className="text-zinc-400"> · {res.domain}</span>}
              {res.cached ? (
                <span className="text-zinc-400"> · {Number(res.total_results).toLocaleString()} paid prospects in your cache{(activeKws.size > 0 || keyword.trim()) ? ` — ${visible.length} match your filters` : ""}{res.excluded_count > 0 ? ` · ${res.excluded_count} hidden by your exclusion list` : ""}</span>
              ) : (
                <>
                  <span className="text-zinc-400"> · {Number(res.total_results).toLocaleString()} {res.job_titles ? `people match your ${res.job_titles.split(",").length} keyword${res.job_titles.split(",").length === 1 ? "" : "s"}` : "people found"}{res.mode === "company" ? " (matched by company name — search the domain directly if this looks wrong)" : ""}</span>
                  {res.already_paid > 0 && <span className="text-amber-600" data-testid="prospect-already-paid"> · {res.already_paid} on this page were already in your paid cache</span>}
                  {res.excluded_count > 0 && <span className="text-rose-500" data-testid="prospect-excluded-note"> · {res.excluded_count} hidden by your exclusion list</span>}
                </>
              )}
            </div>
            {res.domain && (
              <button type="button" onClick={() => setSiteScan({ domain: res.domain, company: (res.organization || importCompany || "").trim() })}
                data-testid="site-scan-btn"
                className="h-8 px-2.5 rounded-md border border-zinc-200 bg-white text-xs font-medium text-zinc-600 hover:border-zinc-400 flex items-center gap-1.5">
                <Globe size={13} /> Scan website for people (free)
              </button>
            )}
            {res.domain && (
              <button type="button" onClick={rateResults} disabled={ratingBusy} data-testid="rate-hunter-btn"
                className="h-8 px-2.5 rounded-md border border-indigo-200 bg-white text-xs font-medium text-indigo-700 hover:border-indigo-400 disabled:opacity-40 flex items-center gap-1.5">
                <Brain size={13} /> {ratingBusy ? "Rating…" : "Rate with AI (ICP)"}
              </button>
            )}
            <div className="ml-auto relative">
              <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="Filter by job title, e.g. market access"
                data-testid="prospect-keyword"
                className="h-8 w-72 rounded-md border border-zinc-200 pl-7 pr-2 text-sm focus:outline-none focus:border-zinc-400" />
            </div>
          </div>

          {res.alt_domain_hint && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex flex-wrap items-center gap-2" data-testid="alt-domain-hint">
              Hunter has almost nothing for {res.domain} — but your {res.alt_domain_hint.contacts} existing {res.alt_domain_hint.company} contacts use <span className="font-mono font-medium">@{res.alt_domain_hint.domain}</span>, so their emails likely live on that domain.
              <button type="button" data-testid="alt-domain-search-btn"
                onClick={() => { setQuery(res.alt_domain_hint.domain); doSearch({ query: res.alt_domain_hint.domain, department, seniority, job_titles: kwString(), offset: 0, limit: PAGE }); }}
                className="px-2 py-0.5 rounded border border-amber-300 bg-white font-medium hover:border-amber-400">
                Search {res.alt_domain_hint.domain} instead
              </button>
            </div>
          )}

          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200"><tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="prospect-select-all" /></th>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Job title</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-left px-3 py-2 font-medium">Confidence</th>
                <th className="text-left px-3 py-2 font-medium">Dept</th>
                <th className="text-left px-3 py-2 font-medium w-10">In</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {visible.map(r => (
                  <tr key={r.email || `${r.first_name}-${r.last_name}`} className={`border-b border-zinc-100 ${!r.importable ? "opacity-60" : ""}`} data-testid={`prospect-row-${r.email}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" disabled={!r.importable} checked={selected.has(r.email)} onChange={() => toggle(r.email)}
                        data-testid={`prospect-check-${r.email}`} title={r.importable ? "" : r.blockers.join(", ")} />
                    </td>
                    <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{r.first_name || r.last_name ? `${r.first_name} ${r.last_name}`.trim() : <span className="text-zinc-400 font-normal">—</span>}{ratings[r.email] && <span className="ml-1.5 align-middle inline-block"><RatingBadge v={ratings[r.email]} /></span>}</td>
                    <td className="px-3 py-2 text-zinc-600 max-w-[260px] truncate" title={r.position}>{r.position || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[12.5px]">{r.email}</td>
                    <td className="px-3 py-2"><ConfBadge v={r.confidence} /></td>
                    <td className="px-3 py-2 text-xs text-zinc-500 capitalize">{r.department || "—"}</td>
                    <td className="px-3 py-2">
                      {r.linkedin && <a href={r.linkedin} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-zinc-400 hover:text-sky-600" title="Open LinkedIn"><LinkedinLogo size={15} /></a>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.in_db ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> in MediHub</span>
                      ) : r.importable ? (
                        <span className="text-zinc-400">ready</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600" title={r.blockers.join(", ")}><WarningCircle size={13} /> {r.blockers[0]}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">{keyword ? "No loaded results match that job-title filter — load more pages or clear the filter." : "No people found."}</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {!res.cached && rows.length < res.total_results && (
              <>
                <button onClick={loadMore} disabled={loadingMore || loadingAll} data-testid="prospect-load-more"
                  className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 disabled:opacity-40">
                  {loadingMore ? "Loading…" : `Load more (${rows.length} of ${Number(res.total_results).toLocaleString()})`}
                </button>
                <button onClick={loadAll} disabled={loadingMore || loadingAll} data-testid="prospect-load-all"
                  className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 disabled:opacity-40">
                  {loadingAll ? `Loading all… (${rows.length} of ${Number(res.total_results).toLocaleString()})` : `Load all ${Number(res.total_results).toLocaleString()} (≈${Math.max(1, Math.ceil((res.total_results - rows.length) / 10))} credit${Math.max(1, Math.ceil((res.total_results - rows.length) / 10)) === 1 ? "" : "s"})`}
                </button>
              </>
            )}
            {selected.size > 0 && (
              <div className="ml-auto flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
                <span className="text-xs text-zinc-500">Import as company:</span>
                <input value={importCompany} onChange={e => setImportCompany(e.target.value)} data-testid="prospect-import-company"
                  className="h-8 w-48 rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:border-zinc-400" />
                <button onClick={doImport} disabled={importing || !importCompany.trim()} data-testid="prospect-import-btn"
                  className="h-8 px-3 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 flex items-center gap-1.5">
                  <DownloadSimple size={14} />{importing ? "Importing…" : `Import ${selected.size} to MediHub`}
                </button>
              </div>
            )}
          </div>

          <p className="text-xs text-zinc-400">Imported contacts get source "hunter", automatic role/level tags from their job title, and appear in <Link to="/contacts" className="underline hover:text-zinc-600">Contacts</Link> immediately. Rows already in MediHub, below 80 confidence, verified invalid, or missing a name can't be selected.</p>

          {res.domain && (
            <ApolloMorePanel key={res.domain} domain={res.domain}
              company={(res.organization || importCompany || "").trim()}
              titles={fitKeywords ?? allKeywords.filter(k => activeKws.has(k))}
              onCreditsUsed={loadApollo}
              ratings={ratings}
              onRequestRate={rateApollo}
              defaultHealthcareOnly={!!(fitScan && fitScan.diversified)} />
          )}
        </>
      )}

      {mode === "global" && !gScanned && !gScanning && (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-white p-12 text-center text-zinc-400">
          <Binoculars size={32} className="mx-auto mb-2" />
          <p className="text-sm">Pick your job-title keywords (e.g. "Clinical trial" + "Clinical research"), choose industries, then Scan — Hunter's whole database, no company needed.</p>
          <p className="text-xs mt-1 text-zinc-400">Scanning is completely free. You only pay 1 credit per person whose email you choose to reveal.</p>
        </div>
      )}

      {mode === "global" && (gScanned > 0 || gScanning) && (
        <>
          <div className="text-sm text-zinc-600" data-testid="global-scan-summary">
            Scanned <span className="font-medium text-zinc-900">{gScanned.toLocaleString()}</span> of ~{Number(gTotal).toLocaleString()} people <span className="text-emerald-700">(free)</span> — <span className="font-medium text-zinc-900">{gRows.length}</span> matching job title{gRows.length === 1 ? "" : "s"}{gFiltered > 0 && <span className="text-zinc-400" data-testid="global-filtered-note"> · {gFiltered} at non-healthcare companies hidden</span>}{gDone ? " · end of results" : ""}
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200"><tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" data-testid="global-select-all"
                    checked={gRows.some(r => !r.imported) && gRows.filter(r => !r.imported && !r.likely_in_db).every(r => gSel.has(r.reveal_handle)) && gSel.size > 0}
                    onChange={() => {
                      const eligible = gRows.filter(r => !r.imported && !r.likely_in_db);
                      const all = eligible.length > 0 && eligible.every(r => gSel.has(r.reveal_handle));
                      setGSel(all ? new Set() : new Set(eligible.map(r => r.reveal_handle)));
                    }} />
                </th>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Job title</th>
                <th className="text-left px-3 py-2 font-medium">Company</th>
                <th className="text-left px-3 py-2 font-medium">Seniority</th>
                <th className="text-left px-3 py-2 font-medium">Email check</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {gRows.map((r, i) => (
                  <tr key={r.reveal_handle || i} className={`border-b border-zinc-100 ${r.imported ? "opacity-60" : ""}`} data-testid={`global-row-${i}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" disabled={r.imported} checked={gSel.has(r.reveal_handle)}
                        onChange={() => setGSel(prev => { const n = new Set(prev); n.has(r.reveal_handle) ? n.delete(r.reveal_handle) : n.add(r.reveal_handle); return n; })}
                        data-testid={`global-check-${i}`} />
                    </td>
                    <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{r.name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-600 max-w-[300px] truncate" title={r.position}>{r.position}</td>
                    <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{(r.company_name || r.domain || "").length <= 4 ? (r.company_name || r.domain || "").toUpperCase() : (r.company_name || r.domain || "").replace(/\b\w/g, c => c.toUpperCase())}<span className="text-xs text-zinc-400"> · {r.domain}</span>
                      {r.domain && (
                        <button type="button" title={`Scan ${r.domain}'s website for more people (free)`}
                          onClick={() => setSiteScan({ domain: r.domain, company: r.company_name || r.domain })}
                          data-testid={`global-site-scan-${i}`} className="ml-1.5 text-zinc-300 hover:text-zinc-600 align-middle"><Globe size={13} /></button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-500 capitalize">{r.seniority || "—"}</td>
                    <td className="px-3 py-2"><VerBadge v={r.verification} /></td>
                    <td className="px-3 py-2 text-xs">
                      {r.imported ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> imported</span>
                      ) : r.likely_in_db ? (
                        <span className="inline-flex items-center gap-1 text-amber-600" title="Someone with this first name and last initial at this company is already in MediHub — revealing may waste a credit"><WarningCircle size={13} /> may already be in MediHub</span>
                      ) : (
                        <span className="text-zinc-400">ready</span>
                      )}
                    </td>
                  </tr>
                ))}
                {gRows.length === 0 && !gScanning && <tr><td colSpan={7} className="p-8 text-center text-sm text-zinc-500">No matching job titles in the people scanned so far — scan more or widen your keywords/industries.</td></tr>}
                {gScanning && <tr><td colSpan={7} className="p-8 text-center text-sm text-zinc-500">Scanning Hunter's database…</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {!gDone && (
              <button onClick={() => scanGlobal(true)} disabled={gScanning || gImporting} data-testid="global-scan-deeper"
                className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 disabled:opacity-40">
                {gScanning ? "Scanning…" : "Scan 1,000 more (free)"}
              </button>
            )}
            {gSel.size > 0 && (
              <button onClick={revealImport} disabled={gImporting || gScanning} data-testid="global-reveal-btn"
                className="ml-auto h-9 px-4 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 flex items-center gap-1.5">
                <DownloadSimple size={14} />{gImporting ? "Revealing…" : `Reveal & import ${gSel.size} to Contacts (≈${gSel.size} credit${gSel.size === 1 ? "" : "s"})`}
              </button>
            )}
          </div>

          <p className="text-xs text-zinc-400">Surnames stay hidden until you reveal. Revealed people are imported straight into <Link to="/contacts" className="underline hover:text-zinc-600">Contacts</Link> with source "hunter" and automatic role/level tags, and saved to your paid cache. Rows flagged "may already be in MediHub" are excluded from select-all — tick them individually if you're sure.</p>
        </>
      )}

      {siteScan && (
        <SitePeopleDialog domain={siteScan.domain} company={siteScan.company}
          keywords={allKeywords.filter(k => activeKws.has(k))}
          onClose={() => setSiteScan(null)} onCreditsUsed={loadAccount} />
      )}
    </div>
  );
}
